import StatusProntuarioBadge, {
  resolveStatusProntuario,
} from 'components/core/StatusProntuarioBadge';
import { usePainel } from 'hooks/usePainel';
import CallHistoryModal from 'pages/painel/HistoricoDeChamadas';
import PainelDoMedico from 'pages/painel/PainelDoMedico';
import { useEffect } from 'react';
import { Col, Row } from 'react-bootstrap';
import { getTodayCallsByDoctor } from 'services/chamadas';
import { getAgendaKey, getDoctorId, isAgendaValid } from 'utils/agendaHelpers';
import { normalizeCallStatus } from 'utils/callStatus';

export default function DadosPaciente({ laudo, agenda }) {
  const {
    callStates,
    setCallStates,
    handleArrivePatient,
    handleDonePatient,
    handleViewCallHistory,
    handleCloseHistoryModal,
    historyState,
  } = usePainel();

  const codAgendaKey = getAgendaKey(agenda);
  const codMed = getDoctorId(agenda, laudo);
  const currentCallState = codAgendaKey ? callStates[codAgendaKey] || null : null;
  const temAgendaValida = isAgendaValid(agenda);

  // Sincroniza o estado da chamada usando a AGENDA
  useEffect(() => {
    let isMounted = true;

    async function syncCallState() {
      if (!codMed || !codAgendaKey) return;

      try {
        const dataHoje = new Date().toISOString().split('T')[0];
        const calls = await getTodayCallsByDoctor(codMed, dataHoje);

        if (!isMounted || !Array.isArray(calls)) return;

        const callEncontrada = calls.find(
          (c) => String(c.CODAGENDA || c.codAgenda) === codAgendaKey
        );

        if (callEncontrada) {
          const rawStatus = callEncontrada.STATUS || callEncontrada.status;

          setCallStates((prev) => ({
            ...prev,
            [codAgendaKey]: {
              callId: callEncontrada.ID || callEncontrada.id,
              status: normalizeCallStatus(rawStatus),
              isLoading: false,
              rawCall: callEncontrada,
            },
          }));
        }
      } catch (err) {
        console.error('Erro ao sincronizar chamada do paciente:', err);
      }
    }

    if (temAgendaValida) {
      syncCallState();
    }

    return () => {
      isMounted = false;
    };
  }, [codMed, codAgendaKey, temAgendaValida, setCallStates]);

  return (
    <div className="p-3 bg-light rounded border">
      <Row className="align-items-center g-3">
        <Col xs={12} md={temAgendaValida ? 8 : 12} lg={temAgendaValida ? 8 : 12}>
          <StatusProntuarioBadge
            status={resolveStatusProntuario(laudo)}
            dataGravacao={laudo?.dataFormatada}
            size="md"
          />
          <div style={{ fontSize: '0.9rem', lineHeight: '1.6' }}>
            <strong>Nome do Paciente: </strong>
            <span className="fs-6 fw-bold">{laudo?.NOMEPAC || agenda?.NOME || '-'}</span>
            &nbsp;&nbsp;|&nbsp;&nbsp;
            <strong>Data Nasc: </strong>
            {laudo?.dataNasc || '-'}
            &nbsp;&nbsp;|&nbsp;&nbsp;
            <strong>Idade: </strong>
            {laudo?.idadePAC ? `${laudo.idadePAC} anos` : '-'}
            <div className="d-flex align-items-center gap-2 flex-wrap">
              <span>
                <strong style={{ color: 'blue' }}>Código Consulta: </strong>
                <span style={{ color: 'blue' }}>{laudo?.CODCONSULTA || '-'}</span>
                &nbsp;&nbsp;|&nbsp;&nbsp;
                <strong style={{ color: 'green' }}>Data: </strong>
                <span style={{ color: 'green' }}>{laudo?.dataFormatada || '-'}</span>
                &nbsp;&nbsp;|&nbsp;&nbsp;
                <strong style={{ color: 'red' }}>Convênio: </strong>
                <span style={{ color: 'red' }}>{laudo?.SIGLACONV || agenda?.SIGLACONV || '-'}</span>
              </span>
            </div>
            <strong>Procedimento: </strong>
            {laudo?.DESCPROCEDI || agenda?.PROCEDIMENTO || '-'}
            &nbsp;&nbsp;|&nbsp;&nbsp;
            <strong>Médico: </strong>Dr. {laudo?.NOMEMED || agenda?.NOMEMED || '-'}
          </div>
        </Col>

        {/* Painel do Médico (Oculto em telas pequenas e exibido apenas com agenda válida) */}
        {temAgendaValida && (
          <Col md={4} lg={4} className="d-none d-md-block border-start text-center">
            <PainelDoMedico
              agenda={agenda}
              callState={currentCallState}
              onArrive={handleArrivePatient}
              onDone={handleDonePatient}
              onViewHistory={handleViewCallHistory}
              isProntuario={true}
            />
          </Col>
        )}
      </Row>

      <CallHistoryModal
        show={historyState.showModal}
        onHide={handleCloseHistoryModal}
        agenda={historyState.agenda}
        call={historyState.call}
        events={historyState.events}
        loading={historyState.isLoading}
        error={historyState.error}
      />
    </div>
  );
}
